import React, { Fragment, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import Header from '../header';
import Footer from '../footer';
import AppErrorBoundary from '../error-boundary';
import { useIdleTimer } from 'react-idle-timer';
import { LOGOUT_REASONS, OKTA_LOGOUT_REASON_KEY } from '../../utils/constants';
import { useSelector, useDispatch } from 'react-redux';
import IdleModal from '../idle-modal';
import VisitExpirationModal from '../visit-expiration-modal';
import { clearVisitExpiration } from '../../pages/cart/VisitExpiration.state';
import { useOktaAuth } from '@okta/okta-react';
import useLogoutTimers from '../../utils/hooks/useLogoutTimers';
import { clearLocalStorageSession } from '../../utils/state';

const Layout = ({ isLoggedIn, hasTerminalSession }) => {
  const { oktaAuth } = useOktaAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const initialTimeLeftToLogoutSeconds = 30;
  const [idleModalOpen, toggleIdleModal] = useState(false);
  const [logoutTimerOn, toggleLogoutTimer] = useState(false);
  const [timeLeftToLogout, setTimeLeft] = useState(
    initialTimeLeftToLogoutSeconds,
  );
  const config = useSelector((state) => state.config);

  const idleLogout = async () => {
    window.clearInterval(logoutTimers.interval);
    dispatch(clearVisitExpiration());
    toggleIdleModal(false);
    toggleLogoutTimer(false);
    localStorage.setItem(
      OKTA_LOGOUT_REASON_KEY,
      LOGOUT_REASONS.INACTIVITY.name,
    );
    setTimeLeft(initialTimeLeftToLogoutSeconds);
    navigate('/logout');
    await oktaAuth.signOut({
      postLogoutRedirectUri: window.location.origin,
    });
    oktaAuth.tokenManager.clear();
    clearLocalStorageSession();
  };

  const handleOnIdle = () => {
    if (!isLoggedIn) return;
    toggleIdleModal(true);
    toggleLogoutTimer(true);
  };

  useIdleTimer({
    timeout: 1000 * 60 * 14.5,
    onIdle: handleOnIdle,
    debounce: 500,
  });

  const logoutTimers = useLogoutTimers({
    logoutTimerOn,
    action: idleLogout,
    timeLeftToLogout,
    handleSetTimeLeft: setTimeLeft,
    initialTimeLeftToLogoutSeconds,
  });
  return (
    <Fragment>
      <Header
        key="main-header"
        isLoggedIn={isLoggedIn}
        hasTerminalSession={hasTerminalSession}
      />
      <AppErrorBoundary config={config} navigate={navigate}>
        <Outlet />
        <VisitExpirationModal />
        <IdleModal
          modalOpen={idleModalOpen}
          toggleModal={toggleIdleModal}
          timeLeftToLogout={timeLeftToLogout}
          totalLogoutTime={initialTimeLeftToLogoutSeconds}
          logout={idleLogout}
          cancelLogout={() => {
            window.clearInterval(logoutTimers.interval);
            window.clearTimeout(logoutTimers.timer);
            setTimeLeft(initialTimeLeftToLogoutSeconds);
            toggleIdleModal(false);
            toggleLogoutTimer(false);
          }}
        />
      </AppErrorBoundary>
      <Footer key="main-footer" />
    </Fragment>
  );
};

export default Layout;
